import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import { MarketingLayout, SectionHeader, I18nPageHero } from "@/components/marketing/Layout";

export const Route = createFileRoute("/about")({
 head: () => {
 const seo = buildSeo({
 path: "/about",
 title: "About Reality Twin — A Ruznic Marketing project",
 description: "The story, founder and principles behind Reality Twin — an AI Twin platform built by Ruznic Marketing in Hinwil.",
 });
 return {
...seo,
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "AboutPage",
 url: "https://realitytwin.io/about",
 name: "About Reality Twin",
 mainEntity: {
 "@type": "Organization",
 name: "Ruznic Marketing",
 founder: { "@type": "Person", name: "Ajdin Ruznic" },
 foundingDate: "2024",
 url: "https://ruznic-marketing.com",
 },
 }),
 },
 ],
 };
 },
 component: AboutPage,
});

function AboutPage() {
 const { t } = useTranslation();
 const principles = t("routes.about.principles", { returnObjects: true }) as { n: string; title: string; desc: string }[];
 const imp = t("routes.about.imprint", { returnObjects: true }) as Record<string, string>;
 return (
 <MarketingLayout>
 <I18nPageHero tKey="pages.about" />

 <section className="py-20 border-b border-border">
 <div className="mx-auto max-w-3xl px-6 space-y-6 text-[16px] leading-[1.7] text-muted-foreground">
 <p>
 {t("routes.about.p1Pre")}
 <a href="https://ruznic-marketing.com" className="underline text-foreground">{t("routes.about.p1Link")}</a>
 {t("routes.about.p1Post")}
 </p>
 <p>
 {t("routes.about.p2Pre")}
 <strong className="text-foreground">{t("routes.about.p2Strong")}</strong>
 {t("routes.about.p2Post")}
 </p>
 <p>{t("routes.about.p3")}</p>
 </div>
 </section>

 <section className="py-20">
 <div className="mx-auto max-w-3xl px-6">
 <p className="font-serif text-[28px] leading-[1.3] tracking-[-0.02em]">
 “{t("routes.about.quote")}”
 </p>
 <div className="mt-6 flex items-center gap-3">
 <div className="h-9 w-9 rounded-full hairline bg-cream flex items-center justify-center text-[13px] font-medium">AR</div>
 <div className="text-[13px]">
 <div className="font-medium">Ajdin Ruznic</div>
 <div className="text-muted-foreground">{t("routes.about.quoteRole")}</div>
 </div>
 </div>
 </div>
 </section>

 <section className="bg-cream border-y border-border py-24">
 <div className="mx-auto max-w-6xl px-6">
 <SectionHeader eyebrow={t("routes.about.principlesEyebrow")} title={<>{t("routes.about.principlesTitleA")}<em className="italic text-muted-foreground">{t("routes.about.principlesTitleEm")}</em></>} />
 <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border hairline rounded-xl overflow-hidden">
 {principles.map((p) => (
 <div key={p.n} className="bg-background p-7 flex gap-6">
 <div className="font-serif text-[40px] leading-none text-muted-foreground/40 shrink-0">{p.n}</div>
 <div>
 <h3 className="font-serif text-[22px] leading-tight">{p.title}</h3>
 <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">{p.desc}</p>
 </div>
 </div>
 ))}
 </div>
 </div>
 </section>

 <section className="py-24">
 <div className="mx-auto max-w-3xl px-6">
 <div className="hairline rounded-xl bg-card p-8 sm:p-10">
 <div className="font-mono-eyebrow">{imp.label}</div>
 <div className="mt-4 grid sm:grid-cols-2 gap-6 text-[14px]">
 <div>
 <div className="text-muted-foreground">{imp.operator}</div>
 <div className="mt-1 font-medium">{imp.operatorName}</div>
 <div className="text-muted-foreground">{imp.ownerLine}</div>
 </div>
 <div>
 <div className="text-muted-foreground">{imp.address}</div>
 <div className="mt-1 whitespace-pre-line">{imp.addressLines}</div>
 </div>
 <div>
 <div className="text-muted-foreground">{imp.status}</div>
 <div className="mt-1">{imp.statusValue}</div>
 </div>
 <div>
 <div className="text-muted-foreground">{imp.contact}</div>
 <div className="mt-1"><a href="/contact" className="underline">{imp.contactCta}</a></div>
 </div>
 </div>
 </div>
 </div>
 </section>
 </MarketingLayout>
 );
}
